import React, { useCallback, useState } from 'react';
import { Controller, FieldPath } from 'react-hook-form';
import { DropEvent, FileRejection } from 'react-dropzone';
import some from 'lodash/some';

import { ErrorMessage } from '../../../types';
import { DropzoneFieldRequiredProps } from './DropzoneField.types';
import { DropzoneHelperFiles } from '../../dropzone/DropzoneHelper';

import {
  DropzoneFieldControl,
  DropzoneFieldControlRequiredProps
} from './components/DropzoneFieldControl';

import { formsErrors } from '../../../locales/keys';

const FILE_INVALID_TYPE = 'file-invalid-type';

interface DropzoneFieldDefaultProps<T> {
  name: FieldPath<T>;
  onChangeFiles?: (files: DropzoneHelperFiles) => void;
}

type DropzoneFieldProps<T> = DropzoneFieldDefaultProps<T> &
  DropzoneFieldRequiredProps<T> &
  DropzoneFieldControlRequiredProps;

function DropzoneField<T>({
  accept,
  control,
  disabled,
  name,
  error,
  type,
  labelFor,
  i18nLabel,
  label,
  labelClassName,
  withoutTabs,
  withoutDownload,
  withoutDropzoneArea,
  withoutRemoveButton,
  withoutStatus,
  maxFiles,
  preventMaxFilesOverload,
  initialFiles,
  initialFileIds,
  onRemoveFile,
  onChangeFiles,
  onDropRejected,
  onFilesAccepted,
  rules,
  syncUpdateAction
}: DropzoneFieldProps<T>) {
  const [fileTypeError, setFileTypeError] = useState<ErrorMessage>();

  const handleDropRejected = useCallback(
    (fileRejections: FileRejection[], event: DropEvent) => {
      const hasInvalidType = some(fileRejections, (rejection) =>
        some(rejection.errors, (err) => err.code === FILE_INVALID_TYPE)
      );
      if (hasInvalidType) {
        setFileTypeError(formsErrors.fileType.invalid);
      }
      onDropRejected?.(fileRejections, event);
    },
    [onDropRejected]
  );

  const handleFilesAccepted = useCallback(() => {
    setFileTypeError(undefined);
    onFilesAccepted?.();
  }, [onFilesAccepted]);

  const displayError = fileTypeError || error;

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange }, fieldState: { error: fieldError } }) => (
        <DropzoneFieldControl
          type={type}
          disabled={disabled}
          error={fieldError?.message || displayError}
          labelFor={labelFor}
          i18nLabel={i18nLabel}
          label={label}
          labelClassName={labelClassName}
          withoutTabs={withoutTabs}
          withoutDownload={withoutDownload}
          withoutDropzoneArea={withoutDropzoneArea}
          withoutRemoveButton={withoutRemoveButton}
          withoutStatus={withoutStatus}
          maxFiles={maxFiles}
          preventMaxFilesOverload={preventMaxFilesOverload}
          syncUpdateAction={syncUpdateAction}
          onChange={onChange}
          initialFiles={initialFiles}
          initialFileIds={initialFileIds}
          onRemoveFile={onRemoveFile}
          accept={accept}
          onChangeFiles={onChangeFiles}
          onDropRejected={handleDropRejected}
          onFilesAccepted={handleFilesAccepted}
        />
      )}
    />
  );
}

export default DropzoneField;
